import { useEffect, useMemo } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { deriveFilteredEmployees } from '@/helpers/deriveFilteredEmployees';
import { useEmployeesMap } from '@/hooks/useEmployeesMap';
import { useGetOrganizationEmployeeDirectoryQuery } from '@/redux/slices/organizationApiSlice';
import { EmployeeDirectoryListing } from '@/types/employees';
import { GetOrganizationEmployeeDirectoryArgs } from '@/types/organizations';

export type EmployeeDirectoryResult = {
  data?: EmployeeDirectoryListing[];
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: FetchBaseQueryError | SerializedError;
};

/**
 * Drop-in for `useGetOrganizationEmployeeDirectoryQuery` in the fields callers
 * read. With the central employee map on, a request it can answer (status and
 * term) takes its rows from the map instead of making another request. A sort
 * keeps the directory request, since the client never sorts (the database
 * collation can't be reproduced in JavaScript), and so does the map being off
 * or a request for another organization (the map only holds the current one).
 *
 * The directory endpoint and the map list the same employees in the same
 * order (`last_name` ASC, `id` DESC) and both are scoped to the caller's
 * divisions, so the rows are the same.
 */
export const useEmployeeDirectory = (
  args: GetOrganizationEmployeeDirectoryArgs | typeof skipToken
): EmployeeDirectoryResult => {
  const employeesMap = useEmployeesMap();

  // Callers pass a new object every render, so memoize on its contents.
  const argsKey = args === skipToken ? undefined : JSON.stringify(args);
  const derivableArgs = useMemo(() => {
    if (argsKey === undefined) {
      return undefined;
    }
    const { organizationId, status, term, orderBy, sortOrder } = JSON.parse(
      argsKey
    ) as GetOrganizationEmployeeDirectoryArgs;
    if (
      organizationId !== employeesMap.organizationId ||
      orderBy !== undefined ||
      sortOrder !== undefined
    ) {
      return undefined;
    }
    const derivable = { status, term };
    return deriveFilteredEmployees([], derivable) ? derivable : undefined;
  }, [argsKey, employeesMap.organizationId]);

  const isFromCentralMap =
    employeesMap.isEnabled && derivableArgs !== undefined;

  const derivedRows = useMemo(() => {
    if (!isFromCentralMap || !employeesMap.hasData || !derivableArgs) {
      return undefined;
    }
    return deriveFilteredEmployees(
      employeesMap.employees,
      derivableArgs
    )?.results.map(
      ({
        id,
        firstName,
        preferredFirstName,
        lastName,
        email,
        jobTitle,
        jobTitleFrCa,
        status,
        startDate,
      }): EmployeeDirectoryListing => ({
        id,
        firstName,
        preferredFirstName,
        lastName,
        email,
        jobTitle,
        jobTitleFrCa,
        status,
        startDate,
      })
    );
  }, [
    isFromCentralMap,
    employeesMap.hasData,
    employeesMap.employees,
    derivableArgs,
  ]);

  const server = useGetOrganizationEmployeeDirectoryQuery(
    isFromCentralMap ? skipToken : args
  );

  // A failed background refetch keeps the last list (below), so report it
  // instead of leaving the staleness invisible.
  const staleListError =
    isFromCentralMap && employeesMap.hasData ? employeesMap.error : undefined;
  useEffect(() => {
    if (staleListError) {
      window.DD_RUM?.addError(
        'Employee directory kept its last list after a failed refetch',
        { location: 'useEmployeeDirectory' }
      );
    }
  }, [staleListError]);

  if (!isFromCentralMap) {
    return {
      data: server.data,
      isFetching: server.isFetching,
      isLoading: server.isLoading,
      isError: server.isError,
      error: server.error as EmployeeDirectoryResult['error'],
    };
  }

  // The map only counts as fetching until its first list, so a background
  // refetch doesn't blank callers that gate on `isFetching`. A failed
  // background refetch keeps the last good list.
  const isWaitingForMap =
    (employeesMap.isLoading || !employeesMap.hasData) && !employeesMap.error;
  const error = employeesMap.hasData ? undefined : employeesMap.error;
  return {
    data: derivedRows,
    isFetching: isWaitingForMap,
    isLoading: isWaitingForMap,
    isError: error !== undefined,
    error,
  };
};
